/** @jsxImportSource @emotion/react */
import { useEffect, useState } from "react";
import { MetadataColumn } from "components/RecordingsGrid/RecordingsGrid.helpers";
import {
  EuiButton,
  EuiButtonEmpty,
  EuiDescriptionList,
  EuiFieldText,
  EuiFormRow,
  EuiModal,
  EuiModalBody,
  EuiModalFooter,
  EuiModalHeader,
  EuiModalHeaderTitle,
} from "@inscopix/ideas-eui";
import { Tooltip } from "components/Tooltip/Tooltip";
import { useDatasetAction } from "hooks/useDatasetAction/useDatasetAction";
import { captureException } from "@sentry/react";
import { ButtonEmptyPermissioned } from "components/ButtonEmptyPermissioned/ButtonEmptyPermissioned";

export type ModalViewMetadataColumnProps = {
  column: MetadataColumn;
  onClose: () => void;
};

export const ModalViewMetadataColumn = ({
  column,
  onClose,
}: ModalViewMetadataColumnProps) => {
  const { colDef } = column;
  const [isEditMode, setIsEditMode] = useState(false);
  const [columnName, setColumnName] = useState(colDef.headerName);
  const updateColumnDefAction = useDatasetAction("updateColumnDef");

  /* Report a warning to Sentry if we find ourselves in an illegal state */

  useEffect(() => {
    if (isEditMode && updateColumnDefAction.isDisabled) {
      captureException("Illegal attempt to edit a metadata column");
    }
  }, [isEditMode, updateColumnDefAction.isDisabled]);

  const saveButtonTooltipMessage = (() => {
    if (columnName === "") {
      return "Enter a column name";
    }

    if (columnName === colDef.headerName) {
      return "Edit at least one value";
    }
  })();

  /**
   * Reverts the fields back to their original state
   */
  const handleCancel = () => {
    setColumnName(colDef.headerName);
    setIsEditMode(false);
  };

  /**
   * Persists changes when the save button is pressed
   */
  const handleSave = () => {
    void updateColumnDefAction.enqueue({
      columnId: column.id,
      colDef: {
        type: "metadata",
        headerName: columnName,
        metadataKey: colDef.metadataKey,
      },
    });
    setIsEditMode(false);
  };

  return (
    <EuiModal onClose={onClose}>
      <EuiModalHeader>
        <EuiModalHeaderTitle>
          {isEditMode ? "Edit Column" : columnName}
        </EuiModalHeaderTitle>
      </EuiModalHeader>
      <EuiModalBody>
        {isEditMode ? (
          <>
            <EuiFormRow
              label="Column name"
              helpText="Custom name for this column"
            >
              <EuiFieldText
                value={columnName}
                autoFocus
                onChange={(e) => setColumnName(e.target.value)}
              />
            </EuiFormRow>
            <EuiFormRow
              label="Key"
              helpText="Key for this column (Not editable)"
            >
              <EuiFieldText value={colDef.metadataKey} disabled />
            </EuiFormRow>
          </>
        ) : (
          <EuiDescriptionList
            listItems={[
              {
                title: "Key",
                description: colDef.metadataKey,
              },
            ]}
          />
        )}
      </EuiModalBody>
      <EuiModalFooter>
        {isEditMode ? (
          <>
            <EuiButtonEmpty onClick={handleCancel}>
              Discard Changes
            </EuiButtonEmpty>
            <Tooltip content={saveButtonTooltipMessage}>
              <EuiButton
                iconType="save"
                onClick={() => void handleSave()}
                fill
                disabled={
                  saveButtonTooltipMessage !== undefined ||
                  updateColumnDefAction.isDisabled
                }
              >
                Save
              </EuiButton>
            </Tooltip>
          </>
        ) : (
          <>
            <ButtonEmptyPermissioned
              iconType="pencil"
              onClick={() => setIsEditMode(true)}
              disabled={updateColumnDefAction.isDisabled}
              requiredPermission="edit"
            >
              Edit
            </ButtonEmptyPermissioned>
            <EuiButton fill onClick={onClose}>
              Close
            </EuiButton>
          </>
        )}
      </EuiModalFooter>
    </EuiModal>
  );
};
